import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Activity, Compass, Users, BookmarkCheck, Megaphone, BookOpen, Handshake,
  Calendar, Heart, FileText, MessageSquare, CreditCard, ShieldCheck, Settings,
  Loader2, Receipt, Printer, Wallet, CheckCircle2,
} from 'lucide-react';
import { DashboardShell } from '../components/brand/DashboardShell';
import { CLAIM_STATUS_META, money, openSuperbill } from '../lib/rcm';
import EntitlementPaywall from '../components/EntitlementPaywall';
import { EntitlementRequiredError } from '../lib/axios-interceptors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const m = CLAIM_STATUS_META[status] || CLAIM_STATUS_META.draft;
  return <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-bold border ${m.cls}`}>{m.label}</span>;
};

export default function PatientBilling() {
  const { getAuthHeader } = useAuth();
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);
  const [focusedClaimId, setFocusedClaimId] = useState(null);
  // Iter 163 · Graceful 402 handling — see FamilyDigest for the pattern.
  const [paywall, setPaywall] = useState(null);

  const load = useCallback(async () => {
    setPaywall(null);
    try {
      const { data } = await axios.get(`${API}/rcm/my-statements`, getAuthHeader());
      setStatements(data || []);
    } catch (e) {
      if (e instanceof EntitlementRequiredError) {
        setPaywall({ flag: e.entitlementRequired, message: e.userMessage });
      } else {
        toast.error('Could not load your statements');
      }
    }
    finally { setLoading(false); }
  }, [getAuthHeader]);

  // Finalize a returning Stripe payment
  const finalizeReturn = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const session = params.get('rcm_session');
    if (params.get('cancelled')) { toast.info('Payment cancelled'); window.history.replaceState({}, '', '/patient/billing'); return; }
    if (!session) return;
    try {
      const { data } = await axios.get(`${API}/rcm/payments/status/${session}`, getAuthHeader());
      if (data.payment_status === 'paid') toast.success('Payment received — thank you!');
    } catch (e) { /* ignore */ }
    window.history.replaceState({}, '', '/patient/billing');
  }, [getAuthHeader]);

  useEffect(() => { (async () => { await finalizeReturn(); await load(); })(); }, [finalizeReturn, load]);

  // Auto-statement deep-link: /patient/billing?claim=<id>[&pay=1] focuses & scrolls
  // to the matching statement card, and optionally auto-launches checkout.
  useEffect(() => {
    if (loading || !statements.length) return;
    const params = new URLSearchParams(window.location.search);
    const claimId = params.get('claim');
    const autoPay = params.get('pay') === '1';
    if (!claimId) return;
    const match = statements.find((c) => c.id === claimId || c.claim_number === claimId);
    if (!match) {
      toast.info('That statement could not be found — it may have been paid already.');
      return;
    }
    setFocusedClaimId(match.id);
    // wait a tick for the card to mount, then scroll
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid="statement-${match.id}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    if (autoPay && Number(match.patient_balance) > 0) {
      payNow(match);
    }
    // Strip query so refresh doesn't repeat the auto-pay
    const url = new URL(window.location.href);
    url.searchParams.delete('claim');
    url.searchParams.delete('pay');
    window.history.replaceState({}, '', url.pathname + url.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, statements]);

  const payNow = async (claim) => {
    setPaying(claim.id);
    try {
      const { data } = await axios.post(`${API}/rcm/claims/${claim.id}/pay`, { origin_url: window.location.origin }, getAuthHeader());
      if (data.url) window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start payment'); setPaying(null); }
  };

  const totalDue = statements.reduce((s, c) => s + Number(c.patient_balance || 0), 0);

  const navItems = [
    { icon: Activity, label: 'Dashboard', href: '/patient/dashboard' },
    { icon: Compass, label: 'Care Navigator', href: '/patient/navigator', highlight: true },
    { icon: Users, label: 'Find Clinicians', href: '/patient/find-clinicians' },
    { icon: BookmarkCheck, label: 'Saved Searches', href: '/patient/saved-searches' },
    { icon: Megaphone, label: 'Post a Need', href: '/patient/care-requests' },
    { icon: BookOpen, label: 'Browse Marketplace', href: '/marketplace' },
    { icon: Handshake, label: 'Offers & Bookings', href: '/patient/offers' },
    { icon: CreditCard, label: 'Billing & Statements', active: true },
    { icon: Heart, label: 'Daily Check-in', href: '/patient/checkin' },
    { icon: Users, label: 'Family Portal', href: '/patient/family' },
    { icon: FileText, label: 'Documents', href: '/patient/documents' },
    { icon: MessageSquare, label: 'Messages', href: '/patient/messages' },
    { icon: ShieldCheck, label: 'Privacy & Data', href: '/privacy/dashboard' },
    { icon: Settings, label: 'Settings', href: '#' },
  ];

  return (
    <DashboardShell
      navItems={navItems}
      title="Billing & statements"
      subtitle="View your visit statements, pay any balance after insurance, and download superbills you can submit to your plan."
      testId="patient-billing-page"
    >
      {loading ? (
        <div className="grid gap-4">{[1, 2].map((i) => <div key={i} className="cn-card h-24 animate-pulse bg-cn-bg/60" />)}</div>
      ) : paywall ? (
        <EntitlementPaywall
          flag={paywall.flag}
          title="Statements aren't available on your account yet"
          message={paywall.message}
          primaryHref="/patient/dashboard"
          primaryLabel="Back to dashboard"
        />
      ) : (
        <>
          <div className="cn-card p-6 mb-6 flex items-center justify-between" data-testid="billing-total-due">
            <div className="flex items-center gap-3">
              <span className="w-12 h-12 rounded-2xl bg-cn-terracotta-50 text-cn-terracotta-600 flex items-center justify-center"><Wallet className="w-6 h-6" /></span>
              <div><p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">Total balance due</p><p className="font-display font-bold text-3xl text-cn-text dark:text-white">{money(totalDue)}</p></div>
            </div>
            <div className="text-right text-sm text-cn-text-muted">{statements.length} statement{statements.length === 1 ? '' : 's'}</div>
          </div>

          {statements.length === 0 ? (
            <div className="cn-card p-12 text-center" data-testid="billing-empty">
              <div className="w-16 h-16 rounded-3xl bg-cn-primary/10 text-cn-primary mx-auto flex items-center justify-center mb-4"><Receipt className="w-8 h-8" /></div>
              <h3 className="cn-display text-xl text-cn-text dark:text-white">No statements yet</h3>
              <p className="text-sm text-cn-text-muted mt-2 max-w-md mx-auto">When a clinician bills for a visit, your statement will appear here.</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="statements-list">
              {statements.map((c) => (
                <div
                  key={c.id}
                  className={`cn-card p-6 transition-all ${focusedClaimId === c.id ? 'ring-2 ring-cn-primary shadow-lg' : ''}`}
                  data-testid={`statement-${c.id}`}
                  data-focused={focusedClaimId === c.id ? 'true' : 'false'}
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2"><h3 className="font-display font-semibold text-lg text-cn-text dark:text-white">{c.clinician_name}</h3><StatusBadge status={c.status} /></div>
                      <p className="text-xs text-cn-text-muted mt-0.5 font-mono">{c.claim_number} · Visit {c.date_of_service}</p>
                      <div className="flex flex-wrap gap-4 mt-3 text-sm">
                        <span className="text-cn-text-muted">Charges <b className="text-cn-text">{money(c.total_charges)}</b></span>
                        <span className="text-cn-text-muted">Insurance <b className="text-cn-text">{money(c.insurance_paid)}</b></span>
                        <span className="text-cn-text-muted">You paid <b className="text-cn-text">{money(c.patient_paid)}</b></span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Balance due</p>
                      <p className="font-display font-bold text-2xl text-cn-terracotta-600">{money(c.patient_balance)}</p>
                      <div className="flex gap-2 mt-3 justify-end">
                        <Button onClick={() => openSuperbill(c)} className="cn-btn cn-btn-ghost !py-2 text-xs" data-testid={`statement-superbill-${c.id}`}><Printer className="w-4 h-4" /> Superbill</Button>
                        {Number(c.patient_balance) > 0 && (
                          <Button onClick={() => payNow(c)} disabled={paying === c.id} className="cn-btn cn-btn-primary !py-2 text-xs" data-testid={`statement-pay-${c.id}`}>
                            {paying === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4" /> Pay now</>}
                          </Button>
                        )}
                        {Number(c.patient_balance) === 0 && c.status === 'paid' && (
                          <span className="inline-flex items-center gap-1 text-cn-primary text-sm font-semibold px-2"><CheckCircle2 className="w-4 h-4" /> Paid</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </DashboardShell>
  );
}
